export type LightColorVariantIconId =
  'blue' | 'red' | 'white' | 'infrared' | 'swir' | 'uv' | 'green' | 'unknown';

const LIGHT_COLOR_ATTRIBUTE_CODE = 'light_color';

const LIGHT_COLOR_SORT_ORDER: Record<LightColorVariantIconId, number> = {
  blue: 0,
  red: 1,
  white: 2,
  infrared: 3,
  swir: 4,
  uv: 5,
  green: 6,
  unknown: 7
};

function normalizeLightColorLabel(label: string): string {
  return label.trim().toLowerCase();
}

function mapNormalizedLightColorLabel(
  normalizedLabel: string
): Exclude<LightColorVariantIconId, 'unknown'> | undefined {
  if (
    normalizedLabel.includes('swir') ||
    normalizedLabel.includes('shortwave infrared') ||
    normalizedLabel.includes('kurzwelliges infrarot')
  ) {
    return 'swir';
  }

  if (
    normalizedLabel.includes('ultraviolet') ||
    normalizedLabel.includes('ultraviolett') ||
    normalizedLabel === 'uv' ||
    normalizedLabel.endsWith('(uv)')
  ) {
    return 'uv';
  }

  if (
    normalizedLabel.includes('infrared') ||
    normalizedLabel.includes('infrarot') ||
    normalizedLabel === 'ir' ||
    normalizedLabel.endsWith('(ir)')
  ) {
    return 'infrared';
  }

  if (normalizedLabel === 'blue' || normalizedLabel === 'blau') {
    return 'blue';
  }

  if (normalizedLabel === 'red' || normalizedLabel === 'rot') {
    return 'red';
  }

  if (normalizedLabel === 'white' || normalizedLabel === 'weiß') {
    return 'white';
  }

  if (
    normalizedLabel === 'green' ||
    normalizedLabel === 'grün' ||
    normalizedLabel === 'gruen'
  ) {
    return 'green';
  }

  return undefined;
}

/**
 * Maps a light color option label to the ProductVariantPickerOption `id`
 * used with `showColor` (CSS modifier: `--circle-blue` / `--circle-red` / …).
 * Unknown labels for `light_color` resolve to `unknown`.
 */
export function getLightColorVariantIconId(
  attributeCode: string,
  label: string
): LightColorVariantIconId | undefined {
  if (attributeCode !== LIGHT_COLOR_ATTRIBUTE_CODE) {
    return undefined;
  }

  const normalizedLabel = normalizeLightColorLabel(label);
  const iconId = mapNormalizedLightColorLabel(normalizedLabel);

  return iconId ?? 'unknown';
}

/**
 * Returns a stable sort index for light color options.
 */
export function getLightColorVariantSortIndex(
  attributeCode: string,
  label: string
): number | undefined {
  const iconId = getLightColorVariantIconId(attributeCode, label);

  if (!iconId) {
    return undefined;
  }

  return LIGHT_COLOR_SORT_ORDER[iconId];
}
